import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/faro";

const MAX_LEN = 200;

/**
 * Nota corta editable inline (2 líneas). Se guarda al perder foco (blur) o
 * al pulsar Enter (⇧Enter inserta salto de línea). Silencia el guardado
 * si el valor no cambió.
 *
 * Props:
 *   endpoint  string  URL PATCH (ej. `${API}/contact/{id}/admin-note`)
 *   value     string  Valor actual persistido
 *   headers   object  { Authorization: ... }
 *   onSaved   fn(newValue) → callback opcional para actualizar la fila
 *   testid    string  base para data-testid (input y wrap)
 */
export function AdminNoteInline({ endpoint, value, headers, onSaved, testid = "admin-note" }) {
  const [text, setText] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const savedRef = useRef(value || "");

  useEffect(() => {
    setText(value || "");
    savedRef.current = value || "";
  }, [value]);

  const commit = async () => {
    const next = (text || "").trim().slice(0, MAX_LEN);
    if (next === (savedRef.current || "").trim()) return;
    setSaving(true);
    try {
      const { data } = await axios.patch(endpoint, { admin_note: next }, { headers });
      const saved = data?.admin_note || "";
      savedRef.current = saved;
      setText(saved);
      if (onSaved) onSaved(saved || null);
      toast.success(saved ? "Nota guardada" : "Nota borrada");
    } catch (e) {
      toast.error("No se pudo guardar la nota");
      setText(savedRef.current);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      e.target.blur();
    }
  };

  const remaining = MAX_LEN - text.length;
  return (
    <div className="w-full" data-testid={`${testid}-wrap`}>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
        onBlur={commit}
        onKeyDown={onKeyDown}
        placeholder="Añadir nota…"
        rows={2}
        maxLength={MAX_LEN}
        aria-label="Nota interna"
        data-testid={testid}
        disabled={saving}
        className="w-full text-[11px] leading-snug rounded-md border border-transparent hover:border-[rgba(30,42,143,0.25)] focus:border-[var(--faro-blue)] focus:bg-white bg-[rgba(30,42,143,0.04)] px-1.5 py-1 outline-none resize-none text-[var(--faro-text)] placeholder:text-[var(--faro-text-soft)] placeholder:italic disabled:opacity-60"
        style={{ minHeight: "2.4em", maxHeight: "3.4em" }}
      />
      {text.length > MAX_LEN - 20 && (
        <div className="text-[9px] text-[var(--faro-text-soft)] text-right pr-1">{remaining}</div>
      )}
    </div>
  );
}

export default AdminNoteInline;
